/**
 * Beta Code Service
 * Handles validation and redemption of beta test codes for Way2Will
 */

// Beta codes that grant unlimited premium access
// Format: CODE -> { description, plan, expiresAt? }
const BETA_CODES: Record<string, {
    description: string;
    plan: 'pro' | 'enterprise';
    expiresAt?: Date;
}> = {
    // Unlimited premium beta codes
    'BETA2026': { description: 'Beta Tester 2026', plan: 'pro' },
    'W2WVIP': { description: 'VIP Access', plan: 'pro' },
    'EARLYBIRD': { description: 'Early Bird Access', plan: 'pro' },
    'FOUNDERSCLUB': { description: 'Founders Club', plan: 'enterprise' },
    'TEAMWAY2WILL': { description: 'Team Access', plan: 'enterprise' },
    'PREMIUMFOREVER': { description: 'Lifetime Premium', plan: 'pro' },
    'UNLIMITEDPRO': { description: 'Unlimited Pro', plan: 'pro' },
    'JUSTINVIP': { description: 'Justin VIP Code', plan: 'enterprise' },
    'INVESTORPASS': { description: 'Investor Access', plan: 'enterprise' },
    'BETATESTER': { description: 'Beta Tester', plan: 'pro' },
};

const STORAGE_KEY = 'way2will_beta_code';

export interface BetaCodeResult {
    valid: boolean;
    plan?: 'pro' | 'enterprise';
    description?: string;
    error?: string;
}

/**
 * Validate a beta code without redeeming it
 */
export function validateBetaCode(code: string): BetaCodeResult {
    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode) {
        return { valid: false, error: 'Please enter a beta code' };
    }

    const betaCodeInfo = BETA_CODES[normalizedCode];

    if (!betaCodeInfo) {
        return { valid: false, error: 'Invalid beta code' };
    }

    // Check expiration if set
    if (betaCodeInfo.expiresAt && new Date() > betaCodeInfo.expiresAt) {
        return { valid: false, error: 'This beta code has expired' };
    }

    return {
        valid: true,
        plan: betaCodeInfo.plan,
        description: betaCodeInfo.description,
    };
}

/**
 * Redeem a beta code and store it
 */
export function redeemBetaCode(code: string): BetaCodeResult {
    const result = validateBetaCode(code);

    if (result.valid) {
        // Store the redeemed code
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            code: code.trim().toUpperCase(),
            plan: result.plan,
            description: result.description,
            redeemedAt: new Date().toISOString(),
        }));
    }

    return result;
}

/**
 * Check if user has a valid redeemed beta code
 */
export function getRedeemedBetaCode(): {
    code: string;
    plan: 'pro' | 'enterprise';
    description: string;
    redeemedAt: string;
} | null {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

/**
 * Clear redeemed beta code
 */
export function clearBetaCode(): void {
    localStorage.removeItem(STORAGE_KEY);
}

/**
 * Get the plan from beta code (for use in auth context)
 */
export function getBetaCodePlan(): 'pro' | 'enterprise' | null {
    const redeemed = getRedeemedBetaCode();
    return redeemed?.plan || null;
}
